import React, { useEffect, useState } from "react";
import { Banknote, HandCoins, Gem, Landmark, Plus, X } from "lucide-react";
import usePieChart from "../../../hooks/usePieChart";
import { bankingPartnerAPI } from "../../../services/BankingPartnerPortal/api";
import { toast } from "react-toastify";

const BankingPatnerDashboard = () => {
  const bankingPartnerId = localStorage.getItem("bankingPartnerId");
  const [loanCategoryData, setLoanCategoryData] = useState([]);
  const [insuranceTypeData, setInsuranceTypeData] = useState([]);
  const [cardStats, setCardStats] = useState([null, null, null, null]);
  const [loading, setLoading] = useState(true);
  const [cardLoading, setCardLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cardError, setCardError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddInsuranceModal, setShowAddInsuranceModal] = useState(false);
  const [newInsuranceTypeName, setNewInsuranceTypeName] = useState("");
  const [isSubmittingInsurance, setIsSubmittingInsurance] = useState(false);
  const [filter, setFilter] = useState("all");

  const icons = [Banknote, HandCoins, Gem, Landmark];
  const labels = [
    "TOTAL REVENUE",
    "TOTAL LOANS DISBURSED",
    "INSURANCE PREMIUM",
    "TOTAL ACCOUNTS OPENED",
  ];

  const subDetails = [
    [], // Revenue
    [], // Loans
    [], // Insurance
    // [ // Accounts Opened
    //   { label: "SAVINGS", value: 170 },
    //   { label: "CURRENT", value: 130 },
    // ],
  ];

  useEffect(() => {
    const fetchPieChartData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await bankingPartnerAPI.getPieChartData(bankingPartnerId);
        if (res.success) {
          setLoanCategoryData(res.data.loanCategoryData || []);

          // Transform insuranceTypeData to [{ label, value }]
          const transformedInsuranceData = (
            res.data.insuranceTypeData || []
          ).map((item) => ({
            label: item.insuranceType,
            value: item.totalValue,
          }));

          setInsuranceTypeData(transformedInsuranceData);
          // setInsuranceTypeData(res.data.insuranceTypeData || []);
        } else {
          setError("Failed to fetch data");
        }
      } catch (err) {
        setError(err.message || "Error fetching data");
      } finally {
        setLoading(false);
      }
    };
    fetchPieChartData();
  }, [bankingPartnerId]);

  useEffect(() => {
    const fetchCardStats = async () => {
      setCardLoading(true);
      setCardError(null);
      try {
        const res = await bankingPartnerAPI.getDashboardCardStats(
          bankingPartnerId
        );
        if (res.success) {
          setCardStats([
            res.data.totalRevenue,
            res.data.totalLoansDisbursed,
            res.data.insurancePremium,
            res.data.totalAccountsOpened,
          ]);
        } else {
          setCardError("Failed to fetch card stats");
        }
      } catch (err) {
        setCardError(err.message || "Error fetching card stats");
      } finally {
        setCardLoading(false);
      }
    };
    fetchCardStats();
  }, [bankingPartnerId]);

  const handleAddCategory = () => {
    setShowAddModal(true);
  };

  const handleSubmitCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Please enter a category name");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await bankingPartnerAPI.addLoanCategory(bankingPartnerId, newCategoryName.trim());
      
      if (res.success) {
        toast.success("Loan category added successfully!");
        setNewCategoryName("");
        setShowAddModal(false);
        
        // Refresh pie chart data
        const refreshRes = await bankingPartnerAPI.getPieChartData(bankingPartnerId);
        if (refreshRes.success) {
          setLoanCategoryData(refreshRes.data.loanCategoryData || []);
        }
      } else {
        toast.error(res.message || "Failed to add loan category");
      }
    } catch (error) {
      toast.error(error.message || "Failed to add loan category");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewCategoryName("");
  };

  const handleAddInsuranceType = () => {
    console.log('Insurance button clicked');
    setShowAddInsuranceModal(true);
  };

  const handleSubmitInsuranceType = async () => {
    if (!newInsuranceTypeName.trim()) {
      toast.error("Please enter an insurance type name");
      return;
    }

    setIsSubmittingInsurance(true);
    try {
      // TODO: Add API method for addInsuranceType
      const newInsuranceType = {
        label: newInsuranceTypeName.trim(),
        value: 0
      };
      setInsuranceTypeData(prev => [...prev, newInsuranceType]);
      
      toast.success("Insurance type added successfully!");
      setNewInsuranceTypeName("");
      setShowAddInsuranceModal(false);
    } catch (error) {
      toast.error(error.message || "Failed to add insurance type");
    } finally {
      setIsSubmittingInsurance(false);
    }
  };

  const handleCloseInsuranceModal = () => {
    setShowAddInsuranceModal(false);
    setNewInsuranceTypeName("");
  };

  const loanPieChart = usePieChart({
    data: loanCategoryData,
    colorRange: [40, 50],
    heading: "Loan Categories",
    defaultPeriod: "all",
    loading,
    customHeight: "h-[420px]",
  });

  const InsuranceTypePieChart = usePieChart({
    data: insuranceTypeData,
    colorRange: [170, 200],
    heading: "Insurance Types",
    defaultPeriod: "all",
    loading,
    customHeight: "h-[420px]",
  });

  // Filter card stats based on selected filter
  const getFilteredCardStats = () => {
    if (filter === "all") return cardStats;
    
    const multiplier = filter === "monthly" ? 0.3 : filter === "quarterly" ? 0.7 : filter === "yearly" ? 0.9 : 1;
    
    return cardStats.map(stat => 
      typeof stat === "number" ? Math.floor(stat * multiplier) : stat
    );
  };

  const filteredCardStats = getFilteredCardStats();

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  return (
    <div className="px-4 md:px-6 mb-4">
      {/* Filter dropdown */}
      <div className="flex justify-end mb-4">
        <select
          value={filter}
          onChange={(e) => handleFilterChange(e.target.value)}
          className="bg-[#1f1f1f] text-gray-300 px-3 py-2 rounded-md text-sm font-medium border border-[#333333] focus:outline-none focus:ring-2 focus:ring-[#FFCA20]"
        >
          <option value="all">All Time</option>
          <option value="yearly">Yearly</option>
          <option value="quarterly">Quarterly</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {labels.map((label, idx) => {
          const Icon = icons[idx];
          const statValue = cardLoading
            ? "..."
            : typeof filteredCardStats[idx] === "number"
            ? `₹${filteredCardStats[idx].toLocaleString("en-IN")}`
            : "₹0";

          return (
            <div
              key={idx}
              className="bg-[#131313] rounded-md p-4 flex justify-between items-start"
            >
              <div className="flex flex-col space-y-2">
                <p className="text-xs text-white/60">{label}</p>
                <p className="text-xl font-semibold text-white">
                  {idx === 3 ? filteredCardStats[idx] ?? "0" : statValue}
                </p>
              </div>
              <div className="bg-yellow-400 rounded-full p-2 flex items-center justify-center">
                <Icon className="w-5 h-5 text-black" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Pie Charts */}
      <div className="flex flex-col lg:flex-row justify-center items-stretch gap-4 w-full mt-6 mb-6">
        <div className="w-full relative">
          {loanPieChart}
          <button
            onClick={handleAddCategory}
            className="absolute top-5 right-20 bg-yellow-400 text-black rounded-full p-1 hover:bg-yellow-500 transition-colors z-10 mr-12"
            title="Add new category"
          >
            <span className="hidden lg:inline">+ add loan category</span>
            <span className="lg:hidden">+ add loan</span>
          </button>
        </div>
        <div className="w-full relative">
          {InsuranceTypePieChart}
          <button
            onClick={handleAddInsuranceType}
            className="absolute top-5 right-20 bg-yellow-400 text-black rounded-full p-1 hover:bg-yellow-500 transition-colors z-10 mr-12"
            title="Add new insurance type"
          >
            <span className="hidden lg:inline">+ add insurance type</span>
            <span className="lg:hidden">+ add type</span>
          </button>
        </div>
      </div>

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#131313] rounded-lg p-6 w-96 max-w-[90vw]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-lg font-semibold">Add New Loan Category</h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-white text-sm mb-2">Category Name</label>
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                className="w-full px-3 py-2 bg-zinc-800 text-white rounded-md border border-gray-600 focus:border-yellow-400 focus:outline-none"
                placeholder="Enter category name"
                disabled={isSubmitting}
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitCategory}
                disabled={isSubmitting || !newCategoryName.trim()}
                className="px-4 py-2 bg-yellow-400 text-black rounded-md hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Adding..." : "Add Category"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Insurance Type Modal */}
      {showAddInsuranceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#131313] rounded-lg p-6 w-96 max-w-[90vw]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-lg font-semibold">Add New Insurance Type</h3>
              <button
                onClick={handleCloseInsuranceModal}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-white text-sm mb-2">Insurance Type Name</label>
              <input
                type="text"
                value={newInsuranceTypeName}
                onChange={(e) => setNewInsuranceTypeName(e.target.value)}
                className="w-full px-3 py-2 bg-zinc-800 text-white rounded-md border border-gray-600 focus:border-yellow-400 focus:outline-none"
                placeholder="Enter insurance type name"
                disabled={isSubmittingInsurance}
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCloseInsuranceModal}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                disabled={isSubmittingInsurance}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitInsuranceType}
                disabled={isSubmittingInsurance || !newInsuranceTypeName.trim()}
                className="px-4 py-2 bg-yellow-400 text-black rounded-md hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmittingInsurance ? "Adding..." : "Add Insurance Type"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankingPatnerDashboard;
